import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock, Loader2, ArrowLeft } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const LoginPage = () => {
    const navigate = useNavigate();
    const { login } = useAuth();
    
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [showForgotPassword, setShowForgotPassword] = useState(false);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotSent, setForgotSent] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            await login(email, password);
            toast.success('Welcome back!');
            navigate('/tracker');
        } catch (err) {
            console.error('Login error:', err);
            setError(err.response?.data?.detail || 'Invalid email or password');
            toast.error('Login failed');
        } finally {
            setIsLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setForgotLoading(true);
        
        try {
            await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
            setForgotSent(true);
            toast.success('Reset instructions sent if account exists');
        } catch (err) {
            toast.error('Failed to send reset email');
        } finally {
            setForgotLoading(false);
        }
    };

    // Forgot Password View
    if (showForgotPassword) {
        return (
            <div className="min-h-screen bg-[#071D35] flex items-center justify-center p-4 pt-20">
                <Card className="w-full max-w-md bg-[#1e293b]/50 backdrop-blur-md border-[#334155]">
                    <CardHeader className="text-center">
                        <div className="flex items-center justify-center mb-4">
                            <img 
                                src="https://customer-assets.emergentagent.com/job_181a1683-971b-4c0d-99ad-dbc6848d04b9/artifacts/2a99u289_AB%20Header%20Logo%20transparent.png"
                                alt="AirlineBound"
                                className="h-12 w-auto object-contain"
                            />
                        </div>
                        <CardTitle className="text-2xl font-bold text-white">Reset Password</CardTitle>
                        <CardDescription className="text-[#94a3b8]">
                            {forgotSent 
                                ? "Check your email for reset instructions"
                                : "Enter your email to receive a reset link"
                            }
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        {forgotSent ? (
                            <div className="text-center space-y-4">
                                <div className="p-4 rounded-lg bg-green-500/10 border border-green-500/30 text-green-400">
                                    If an account exists with this email, you'll receive reset instructions shortly.
                                </div>
                                <Button
                                    onClick={() => {
                                        setShowForgotPassword(false);
                                        setForgotSent(false);
                                        setForgotEmail('');
                                    }}
                                    variant="outline"
                                    className="border-[#334155] text-white hover:bg-[#334155]"
                                >
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    Back to Login
                                </Button>
                            </div>
                        ) : (
                            <form onSubmit={handleForgotPassword} className="space-y-4">
                                <div className="space-y-2">
                                    <Label htmlFor="forgot-email" className="text-[#94a3b8]">Email</Label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#64748b]" />
                                        <Input
                                            id="forgot-email"
                                            type="email"
                                            placeholder="you@example.com"
                                            value={forgotEmail}
                                            onChange={(e) => setForgotEmail(e.target.value)}
                                            className="pl-10 bg-[#071D35] border-[#334155] text-white placeholder:text-[#64748b] focus:border-[#f07050]"
                                            required
                                        />
                                    </div>
                                </div>

                                <Button
                                    type="submit"
                                    disabled={forgotLoading}
                                    className="w-full bg-[#f07050] hover:bg-[#e06040] text-white"
                                >
                                    {forgotLoading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Sending...
                                        </>
                                    ) : (
                                        'Send Reset Link'
                                    )}
                                </Button>

                                <Button
                                    type="button"
                                    onClick={() => setShowForgotPassword(false)}
                                    variant="ghost"
                                    className="w-full text-[#94a3b8] hover:text-white"
                                >
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    Back to Login
                                </Button>
                            </form>
                        )}
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#071D35] flex items-center justify-center p-4 pt-20">
            <Card className="w-full max-w-md bg-[#1e293b]/50 backdrop-blur-md border-[#334155]">
                <CardHeader className="text-center">
                    <div className="flex items-center justify-center mb-4">
                        <img 
                            src="https://customer-assets.emergentagent.com/job_181a1683-971b-4c0d-99ad-dbc6848d04b9/artifacts/2a99u289_AB%20Header%20Logo%20transparent.png"
                            alt="AirlineBound"
                            className="h-12 w-auto object-contain"
                        />
                    </div>
                    <CardTitle className="text-2xl font-bold text-white">Welcome Back</CardTitle>
                    <CardDescription className="text-[#94a3b8]">
                        Sign in to continue tracking flights
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        {error && (
                            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                                {error}
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-[#94a3b8]">Email</Label>
                            <div className="relative">
                                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#64748b]" />
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="you@example.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="pl-10 bg-[#071D35] border-[#334155] text-white placeholder:text-[#64748b] focus:border-[#f07050]"
                                    required
                                    data-testid="login-email-input"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <div className="flex justify-between items-center">
                                <Label htmlFor="password" className="text-[#94a3b8]">Password</Label>
                                <button
                                    type="button"
                                    onClick={() => setShowForgotPassword(true)}
                                    className="text-xs text-[#f07050] hover:underline"
                                    data-testid="forgot-password-link"
                                >
                                    Forgot password?
                                </button>
                            </div>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[#64748b]" />
                                <Input
                                    id="password"
                                    type="password"
                                    placeholder="••••••••"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="pl-10 bg-[#071D35] border-[#334155] text-white placeholder:text-[#64748b] focus:border-[#f07050]"
                                    required
                                    data-testid="login-password-input"
                                />
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={isLoading}
                            className="w-full bg-[#f07050] hover:bg-[#e06040] text-white btn-primary-glow"
                            data-testid="login-submit-btn"
                        >
                            {isLoading ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Signing in...
                                </>
                            ) : (
                                'Sign In'
                            )}
                        </Button>
                    </form>

                    <div className="mt-6 text-center">
                        <p className="text-[#94a3b8] text-sm">
                            Don't have an account?{' '}
                            <Link 
                                to="/register" 
                                className="text-[#f07050] hover:underline"
                                data-testid="login-register-link"
                            >
                                Sign up
                            </Link>
                        </p>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
};
